import { checkoutTimeout } from "../api/checkoutTimeout";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useLocale } from "../contexts/LocaleContext";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import {
  CheckoutUnavailableError,
  abortOrderCheckout,
  createStripeCheckoutSession,
  ensureStripeJs,
  getStripeCheckoutSessionForResume,
} from "../api/stripeCheckout";
import { resolvePrice } from "../lib/price";
import "./styles/payment-overlay.css";

type StripeCheckoutCtrl = {
  mount: (el: HTMLElement | string) => void;
  destroy: () => void;
};

type StripeFactory = (
  publishableKey: string,
  options?: Record<string, unknown>,
) => {
  initEmbeddedCheckout: (opts: {
    clientSecret: string;
    onComplete?: () => void;
  }) => Promise<StripeCheckoutCtrl>;
};

/**
 * Stripe Embedded Checkout — behaviour aligned with
 * `_legacy/static/pages/payment.html`
 * (`create-checkout-session`, `initEmbeddedCheckout`, overlay, resume, cancel).
 */
export function PaymentPage() {
  const { items, total, count, clear } = useCart();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { t, formatMoney, currency } = useLocale();

  const [donationStr, setDonationStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  /** Backdrop + modal container active. */
  const [overlay, setOverlay] = useState(false);
  /** True once Stripe Embedded Checkout mounts (hides spinner text). */
  const [stripeFormMounted, setStripeFormMounted] = useState(false);
  /** Enables “Zahlung abbrechen” once a Stripe session ID exists locally. */
  const [abortAllowed, setAbortAllowed] = useState(false);

  const cartSectionRef = useRef<HTMLDivElement | null>(null);
  const stripeMountRef = useRef<HTMLDivElement | null>(null);
  const checkoutControllerRef = useRef<StripeCheckoutCtrl | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  /** Prevents concurrent `initEmbeddedCheckout` mounts. */
  const mountGuardRef = useRef(false);

  /** One resume attempt per page load unless we reset after failure/teardown. */
  const resumeTriedRef = useRef(new Set<string>());
  const operationRef = useRef(0);
  const retrySessionRef = useRef<string | null>(null);

  const donation = useMemo(() => {
    const n = parseFloat(donationStr.replace(",", "."));
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [donationStr]);

  const grandTotal = total + donation;

  useEffect(() => {
    if (!isAuthenticated) {
      sessionStorage.setItem("postLoginRedirect", "payment");
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(
    () => () => {
      operationRef.current += 1;
      mountGuardRef.current = false;
      resumeTriedRef.current.clear();
      try {
        checkoutControllerRef.current?.destroy();
      } catch {
        /* ignore */
      }
      checkoutControllerRef.current = null;
    },
    [],
  );

  const teardownStripeUi = useCallback(() => {
    operationRef.current += 1;
    mountGuardRef.current = false;
    sessionIdRef.current = null;
    setStripeFormMounted(false);
    setAbortAllowed(false);
    setOverlay(false);

    try {
      checkoutControllerRef.current?.destroy();
    } catch {
      /* ignore */
    }
    checkoutControllerRef.current = null;

    const mount = stripeMountRef.current;
    if (mount) mount.replaceChildren();

    if (cartSectionRef.current) cartSectionRef.current.style.display = "block";
  }, []);

  const bindEmbeddedCheckout = useCallback(
    async (clientSecret: string, publishableKey: string, sessionId: string, operation: number) => {
      await checkoutTimeout(ensureStripeJs());
      if (operation !== operationRef.current) return;
      const StripeFn = (window as unknown as { Stripe?: StripeFactory })
        .Stripe;
      if (!StripeFn) throw new Error(t("payment.stripeUnavailable"));

      setOverlay(true);
      if (cartSectionRef.current) cartSectionRef.current.style.display = "none";
      const stripe = StripeFn(publishableKey);
      sessionIdRef.current = sessionId;

      const initialization = stripe.initEmbeddedCheckout({
        clientSecret,
        onComplete: () => {
          if (operation !== operationRef.current) return;
          retrySessionRef.current = null;
          teardownStripeUi();
          void (async () => {
            try {
              await clear();
            } catch (err) {
              console.error("Cart clear after payment:", err);
            }
            navigate(
              `/payment-success?session_id=${encodeURIComponent(sessionId)}`,
              { replace: true },
            );
          })();
        },
      });

      // A cancelled or timed-out initialization may still resolve later.
      void initialization.then((controller) => {
        if (operation !== operationRef.current) {
          try { controller.destroy(); } catch { /* already destroyed */ }
        }
      }, () => undefined);
      const checkout = await checkoutTimeout(initialization);
      if (operation !== operationRef.current) return;
      checkoutControllerRef.current = checkout;
      const el = stripeMountRef.current;
      if (!el) throw new Error(t("payment.noStripeContainer"));
      checkout.mount(el);
      setStripeFormMounted(true);
    },
    [navigate, teardownStripeUi, clear, t],
  );

  const resumeSession = useCallback(
    async (existingSessionId: string) => {
      if (
        mountGuardRef.current ||
        !existingSessionId ||
        count === 0
      ) {
        return;
      }

      resumeTriedRef.current.add(existingSessionId);
      const operation = ++operationRef.current;
      mountGuardRef.current = true;
      sessionIdRef.current = existingSessionId;
      setAbortAllowed(true);

      setPaymentError(null);
      setBusy(true);

      try {
        const data =
          await getStripeCheckoutSessionForResume(existingSessionId);
        if (operation !== operationRef.current) return;
        retrySessionRef.current = existingSessionId;
        const clientSecret = data.clientSecret;
        const publishableKey = data.publishableKey;
        if (!clientSecret || !publishableKey) {
          throw new Error(
            typeof data.error === "string"
              ? data.error
              : t("payment.sessionResumeFailed"),
          );
        }
        await bindEmbeddedCheckout(
          clientSecret,
          publishableKey,
          existingSessionId,
          operation,
        );
      } catch (err) {
        if (operation !== operationRef.current) return;
        if (err instanceof CheckoutUnavailableError) retrySessionRef.current = null;
        console.error("Stripe resume:", err);
        setPaymentError((err as Error).message);
        mountGuardRef.current = false;
        teardownStripeUi();
      } finally {
        if (operation === operationRef.current || !mountGuardRef.current) setBusy(false);
      }
    },
    [bindEmbeddedCheckout, count, teardownStripeUi, toast, t],
  );

  useEffect(() => {
    const sid =
      typeof user?.stripe_session_id === "string"
        ? user.stripe_session_id
        : null;
    if (!sid || count === 0 || resumeTriedRef.current.has(sid)) return;
    void resumeSession(sid);
  }, [user?.stripe_session_id, count, resumeSession]);

  const startCheckout = async () => {
    if (count === 0) {
      toast.show(t("cart.empty"), "danger");
      return;
    }

    if (mountGuardRef.current) {
      return;
    }

    if (retrySessionRef.current) {
      await resumeSession(retrySessionRef.current);
      return;
    }
    const operation = ++operationRef.current;
    mountGuardRef.current = true;
    setPaymentError(null);
    setBusy(true);

    try {
      const data = await createStripeCheckoutSession(
        items,
        currency,
        donation > 0 ? donation : null,
      );
      const clientSecret = data.clientSecret;
      const publishableKey = data.publishableKey;
      const sessionId = data.sessionId;
      if (!clientSecret || !publishableKey || !sessionId) {
        throw new Error(
          typeof data.error === "string"
            ? data.error
            : t("payment.checkoutIncomplete"),
        );
      }

      if (operation !== operationRef.current) return;
      sessionIdRef.current = sessionId;
      retrySessionRef.current = sessionId;
      setAbortAllowed(true);
      await bindEmbeddedCheckout(clientSecret, publishableKey, sessionId, operation);
    } catch (err) {
      if (operation !== operationRef.current) return;
      console.error("startCheckout", err);
      setPaymentError((err as Error).message);
      mountGuardRef.current = false;
      teardownStripeUi();
    } finally {
      if (operation === operationRef.current || !mountGuardRef.current) setBusy(false);
    }
  };

  const onCancelPayment = async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    try {
      const res = await abortOrderCheckout(sid);
      if (!res.ok) {
        toast.show(t("payment.cancelFailed"), "danger");
        return;
      }
    } catch (e) {
      toast.show(`${(e as Error).message}`, "danger");
      return;
    }

    retrySessionRef.current = null;
    teardownStripeUi();
    toast.show(t("payment.cancelled"), "info");
  };

  return (
    <div className="content dwik-payment-page">
      <h1>{t("payment.title")}</h1>

      <div
        className="dwik-payment-backdrop"
        style={{ display: overlay ? "block" : "none" }}
        aria-hidden="true"
      />

      <div
        className="dwik-payment-overlay"
        style={{ display: overlay ? "flex" : "none" }}
        aria-modal="true"
        role="dialog"
        aria-label={t("payment.stripeDialog")}
      >
        <div className="dwik-payment-overlay-inner">
          <div
            id="payment-loading-message"
            style={{
              display: stripeFormMounted || !busy ? "none" : "block",
            }}
          >
            <h3>{t("payment.loadingForm")}</h3>
          </div>

          <div ref={stripeMountRef} id="stripe-checkout-container" />

          <button
            type="button"
            className="dwik-cancel-payment-btn"
            disabled={!abortAllowed || !overlay}
            onClick={() => void onCancelPayment()}
          >
            {t("payment.cancelPayment")}
          </button>
        </div>
      </div>

      <div className="framed" style={{ display: "block", width: "auto" }}>
        <div ref={cartSectionRef} id="cart-section">
          <h3>{t("payment.yourFrames")}</h3>
          {busy && !overlay && <p role="status">{t("payment.preparing")}</p>}
          {paymentError && <p className="payment-error" role="alert">{paymentError}</p>}

          <div id="cart-items" style={{ marginBottom: 20 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "0.75rem" }}>
                    {t("payment.frameCol")}
                  </th>
                  <th style={{ textAlign: "left", padding: "0.75rem" }}>
                    {t("payment.locationCol")}
                  </th>
                  <th style={{ textAlign: "right", padding: "0.75rem" }}>
                    {t("common.price")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.values(items).map((p) => (
                  <tr key={p.pixel}>
                    <td style={{ padding: "0.75rem" }}>{p.pixel}</td>
                    <td style={{ padding: "0.75rem" }}>
                      {p.location || t("common.dash")}
                    </td>
                    <td style={{ padding: "0.75rem", textAlign: "right" }}>
                      {formatMoney(
                        resolvePrice(p.region?.properties?.price, currency),
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr />

          <div className="input-group">
            <label htmlFor="spende">{t("payment.donation")} </label>
            <span className="input-group-text" id="currency-addon">
              {currency}
            </span>
            <input
              type="text"
              className="form-control"
              id="spende"
              placeholder="0.00"
              value={donationStr}
              disabled={overlay}
              onChange={(e) => setDonationStr(e.target.value)}
              aria-describedby="currency-addon"
            />
          </div>

          <p id="gesamt-mit-spende">
            <strong>{t("cart.total")}</strong>{" "}
            <span id="total-with-donation">{formatMoney(grandTotal)}</span>
          </p>

          <div className="zahloptionen">
            <button
              id="checkout-button"
              className="btn-kaufen"
              style={{ marginBottom: 20 }}
              disabled={busy || count === 0 || overlay}
              type="button"
              onClick={() => void startCheckout()}
            >
              {busy ? t("payment.loading") : t("payment.payNow")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
